import { useState, useRef, useEffect, useId } from "react";
import type { KeyboardEvent } from "react";
import { cn } from "../lib/utils";

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Select option…",
  disabled = false,
  className = "",
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const selected = options.find(o => o.value === value) ?? null;
  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    function onMouse(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onMouse);
    return () => document.removeEventListener("mousedown", onMouse);
  }, []);

  function handleOpen() {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    setActiveIdx(-1);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function select(opt: ComboboxOption) {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") { setOpen(false); setQuery(""); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0 && filtered[activeIdx] && !filtered[activeIdx].disabled) {
      select(filtered[activeIdx]);
    }
  }

  return (
    <div ref={rootRef} className={cn("combobox-root", className)}>
      <button
        type="button"
        className={cn("combobox-trigger", open && "combobox-trigger-open")}
        disabled={disabled}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-owns={listId}
      >
        <span className={cn("combobox-value-text", !selected && "combobox-placeholder")}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="combobox-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="combobox-dropdown" role="listbox" id={listId}>
          <div className="combobox-search-wrap">
            <svg className="combobox-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              className="combobox-search"
              placeholder="Search…"
              value={query}
              onChange={e => { setQuery(e.target.value); setActiveIdx(-1); }}
              onKeyDown={handleKey}
            />
          </div>
          <div className="combobox-list">
            {filtered.length === 0 && (
              <p className="combobox-empty">No results for &ldquo;{query}&rdquo;</p>
            )}
            {filtered.map((opt, i) => (
              <button
                key={opt.value}
                className={cn(
                  "combobox-option",
                  opt.value === value && "combobox-option-selected",
                  activeIdx === i && "combobox-option-highlighted",
                  opt.disabled && "combobox-option-disabled",
                )}
                onClick={() => select(opt)}
                disabled={opt.disabled}
                role="option"
                aria-selected={opt.value === value}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <span className="combobox-option-check">
                  {opt.value === value && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
